// @ts-nocheck

import React, { useCallback, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import SpinePlayer from '../../../../components/SpinePlayer';
import theme from '../../../../styled-components/dark';
import { SpineAnimationIds } from '../../../../types/animations';
import { SoundIds } from '../../../../types/sounds';
import KLCollectibleBetslipDetailsCardContainer from '../../containers/KLOrientationStepDetailsCardContainer';
import { KLCollectibleStatuses } from '../../types/collectible';

import KLPoleTimer from './KLPoleTimer';

const STAGE_TIME = {
  '1': 30, // min
  '2': 1 * 60, // min
  '3': 2 * 60, // min
  '4': 4 * 60, // min
  '5': 6 * 60, // min
};

const ANIMATION_DURATION_MS = 1500;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
  height: 100%;
  padding-bottom: 3em;

  & > * {
    z-index: 2;
  }
`;

const fadeOutAnimation = keyframes`
  0% { opacity: 1 };
  100% { opacity: 0 };
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: -10%;
`;

const BetslipDetailsWrapper = styled.div`
  width: 100%;
  height: 34%;

  opacity: 0;
  animation: ${fadeOutAnimation} 1s ease 0.5s 1 reverse forwards;
`;

const StyledKLCollectibleBetslipDetailsCardContainer = styled(KLCollectibleBetslipDetailsCardContainer)`
  align-items: flex-start;
`;

const popUpAnimation = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(1.3) translateY(-3.5em);
  }
`;

const AnimatedTime = styled.div`
  position: absolute;

  color: ${theme.KLNotification.highlightColor};
  font-weight: bold;
  font-size: 1.4em;
  font-family: 'BettingArchivoNarrow';

  opacity: 0;
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${popUpAnimation} ${ANIMATION_DURATION_MS}ms ease;
    `}
`;

const popFadeInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const AnimatedText = styled.div`
  text-transform: uppercase;
  text-align: center;
  font-size: 2em;

  opacity: 0;
  ${({ $animate, $delay }) =>
    $animate &&
    css`
      animation: ${popFadeInAnimation} ${ANIMATION_DURATION_MS}ms ease ${$delay};
      animation-fill-mode: forwards;
    `}
`;

const SmallText = styled.div`
  font-size: 0.6em;
  text-transform: none;
`;

type KLNotificationPoleProps = {
  className?: string;
  previousTime?: number;
  totalTime: number;
  hasWon?: boolean;
  betslipReference: string;
};

const KLNotificationPole = ({
  className,
  previousTime,
  totalTime,
  hasWon,
  betslipReference,
}: KLNotificationPoleProps) => {
  const prevTime = previousTime ?? totalTime;

  const [animateText, setAnimateText] = useState(false);
  const [timerValue, setTimerValue] = useState(prevTime);

  const timeDiff = useMemo(() => totalTime - prevTime, [prevTime, totalTime]);
  const animationId = useMemo(
    () => (hasWon ? SpineAnimationIds.KL_POLE_BET_WON : SpineAnimationIds.KL_POLE_BET_LOST),
    [hasWon],
  );

  const isStage = useCallback(
    (stageNumber: string) => prevTime < STAGE_TIME[stageNumber] && totalTime >= STAGE_TIME[stageNumber],
    [prevTime, totalTime],
  );

  const incrementTimer = useCallback(() => {
    let timeToAdd = 1;
    const incrementTime = (ANIMATION_DURATION_MS * 0.75) / timeDiff;

    const timer = setInterval(() => {
      setTimerValue(prevTime + timeToAdd++);

      if (timeToAdd > timeDiff) {
        clearInterval(timer);
      }
    }, incrementTime);
  }, [timeDiff, prevTime]);

  const onStartAnimation = useCallback(() => {
    setAnimateText(false);
  }, []);

  const onEndAnimation = useCallback(() => {
    setAnimateText(true);

    if (hasWon) {
      incrementTimer();
    }
  }, [hasWon, incrementTimer]);

  const soundId = useMemo(() => {
    if (hasWon) {
      if (isStage('5')) {
        return SoundIds.KL_POLE_WON_STAGE_5;
      } else if (isStage('4')) {
        return SoundIds.KL_POLE_WON_STAGE_4;
      } else if (isStage('3')) {
        return SoundIds.KL_POLE_WON_STAGE_3;
      } else if (isStage('2')) {
        return SoundIds.KL_POLE_WON_STAGE_2;
      } else if (isStage('1')) {
        return SoundIds.KL_POLE_WON_STAGE_1;
      } else {
        return SoundIds.KL_POLE_WON;
      }
    } else {
      return SoundIds.KL_POLE_LOST;
    }
  }, [hasWon, isStage]);

  return (
    <Wrapper className={className}>
      <BetslipDetailsWrapper>
        <StyledKLCollectibleBetslipDetailsCardContainer
          betslipReference={betslipReference}
          status={hasWon ? KLCollectibleStatuses.WON : KLCollectibleStatuses.LOST}
        />
      </BetslipDetailsWrapper>
      <Content>
        <SpinePlayer
          animationId={animationId}
          soundId={soundId}
          scale={0.4}
          onStartAnimation={onStartAnimation}
          onEndAnimation={onEndAnimation}
        />
        {hasWon ? (
          <>
            <AnimatedTime $animate={animateText}>+{timeDiff} min</AnimatedTime>
            <AnimatedText $animate={animateText} $delay={`${ANIMATION_DURATION_MS / 4}ms`}>
              Bravo ! {isStage('5') ? <SmallText> Vous êtes un héros de Koh-Lanta, continuez !</SmallText> : null}
            </AnimatedText>
          </>
        ) : (
          <AnimatedText $animate={animateText}>Raté</AnimatedText>
        )}
      </Content>
      <KLPoleTimer time={timerValue} />
    </Wrapper>
  );
};

export default KLNotificationPole;
